// src/pages/Subscribe.js
// Subscription plans (free / basic / premium) via Razorpay.
// Sibling page is Credits.js — the header toggle switches between them.

import { useEffect, useState } from "react";
import { subscription as subApi } from "../api";
import { openCheckout } from "../lib/razorpay";

export default function Subscribe({ user, onUser, onNavigate }) {
  const [plans, setPlans] = useState([]);
  const [keyId, setKeyId] = useState("");
  const [busy, setBusy] = useState(null); // tier in-flight
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const current = user?.subscription_tier || "free";

  useEffect(() => {
    subApi
      .plans()
      .then((d) => {
        setPlans(d.plans || []);
        setKeyId(d.razorpay_key_id || "");
      })
      .catch((e) => setErr(e.message));
  }, []);

  const upgrade = async (tier) => {
    setErr("");
    setMsg("");
    setBusy(tier);
    try {
      const order = await subApi.createOrder(tier);
      const resp = await openCheckout({
        key: order.key || keyId,
        amount: order.amount, // subscription create-order returns paise already
        currency: "INR",
        orderId: order.order_id,
        name: "Karvi AI",
        description: `${tier} plan`,
        prefill: { email: user?.email, name: user?.username },
      });
      const verified = await subApi.verify({
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
        tier,
      });
      setMsg(`✓ You're on the ${verified.subscription_tier} plan.`);
      onUser?.((u) => (u ? { ...u, subscription_tier: verified.subscription_tier } : u));
    } catch (e) {
      if (e.message !== "Payment cancelled") setErr(e.message);
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel subscription? You keep access until it expires.")) return;
    try {
      const r = await subApi.cancel();
      setMsg(r.message || "Subscription cancelled.");
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div className="page">
      <div className="billing__tabs">
        <button className="billing__tab" onClick={() => onNavigate("credits")}>Buy credits</button>
        <button className="billing__tab billing__tab--on">Plans</button>
      </div>

      {msg && <div className="form-note">{msg}</div>}
      {err && <div className="form-error">{err}</div>}

      <div className="plans">
        {plans.map((p) => {
          const isCurrent = p.tier === current;
          const isFree = p.tier === "free";
          return (
            <div key={p.tier} className={`plan ${p.tier === "premium" ? "plan--feat" : ""} ${isCurrent ? "plan--current" : ""}`}>
              {p.tier === "premium" && <span className="plan__tag">Best value</span>}
              <div className="plan__name">{p.name}</div>
              <div className="plan__price">{p.price === 0 ? "Free" : <>₹{p.price}<small>/{p.duration_days || 15}d</small></>}</div>
              <ul className="plan__features">
                {(p.features || []).map((f, i) => (
                  <li key={i}>✓ {f}</li>
                ))}
              </ul>
              {isCurrent ? (
                <button className="btn btn--ghost btn--block" disabled>Current plan</button>
              ) : isFree ? (
                <button className="btn btn--ghost btn--block" disabled>—</button>
              ) : (
                <button className="btn btn--primary btn--block" onClick={() => upgrade(p.tier)} disabled={busy === p.tier}>
                  {busy === p.tier ? "Opening…" : `Upgrade to ${p.name}`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {current !== "free" && (
        <button className="btn btn--ghost cancel-sub" onClick={cancel}>Cancel subscription</button>
      )}

      <p className="fineprint">Plans renew every 15 days. Premium unlocks 18+ content (age-verified accounts only).</p>
    </div>
  );
}
